import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Trash2,
    Receipt,
    DollarSign,
    Calendar,
    CreditCard,
    FileText,
    AlertCircle,
    Loader,
    User,
    ShoppingCart,
    Package
} from 'lucide-react';
import { receiptsAPI } from '../../services/garageApi';

const ReceiptDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchReceipt = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await receiptsAPI.show(id);
            const data = response.data;

            if (!data) {
                throw new Error('Receipt not found');
            }

            setReceipt(data);
        } catch (err) {
            console.error('Error fetching receipt:', err);
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load receipt');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchReceipt();
    }, [fetchReceipt]);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this receipt? This may affect the payment status of the related transaction.')) {
            return;
        }

        try {
            await receiptsAPI.delete(id);
            alert('Receipt deleted successfully');
            navigate('/receipts');
        } catch (err) {
            console.error('Error deleting receipt:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Failed to delete receipt';
            alert('Error: ' + message);
        }
    };

    const getTypeBadge = (type) => {
        const config = {
            sale: { color: 'bg-green-100 text-green-800', label: 'Sale Payment' },
            purchase: { color: 'bg-blue-100 text-blue-800', label: 'Purchase Payment' },
        };
        return config[type] || { color: 'bg-gray-100 text-gray-800', label: type || 'Unknown' };
    };

    const getPaymentMethodLabel = (method) => {
        const labels = {
            cash: 'Cash',
            cheque: 'Cheque',
            card: 'Card',
            bank_transfer: 'Bank Transfer',
            credit_card: 'Credit Card',
        };
        return labels[method] || method || '-';
    };

    const getPaymentMethodIcon = (method) => {
        switch (method) {
            case 'cash': return DollarSign;
            case 'cheque': return FileText;
            case 'card':
            case 'credit_card': return CreditCard;
            case 'bank_transfer': return CreditCard;
            default: return DollarSign;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading receipt...
                </div>
            </div>
        );
    }

    if (error || !receipt) {
        return (
            <div className="space-y-4">
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error || 'Receipt not found'}</span>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/receipts')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Receipts
                </button>
            </div>
        );
    }

    const typeBadge = getTypeBadge(receipt.type);
    const MethodIcon = getPaymentMethodIcon(receipt.payment_method);
    const receiptable = receipt.receiptable;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => navigate('/receipts')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Receipt {receipt.id}</h1>
                        <p className="text-gray-600">
                            {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${typeBadge.color}`}>
                        {typeBadge.label}
                    </span>
                    <button
                        onClick={handleDelete}
                        className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                    </button>
                </div>
            </div>

            {/* Receipt Info Card */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center">
                        <Receipt className="h-4 w-4 mr-2" />
                        Receipt Details
                    </h3>
                </div>
                <div className="px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Amount */}
                        <div className="bg-green-50 rounded-lg p-4">
                            <div className="flex items-center text-sm text-green-600 mb-1">
                                <DollarSign className="h-4 w-4 mr-1" />
                                Amount
                            </div>
                            <div className="text-3xl font-bold text-green-900">
                                ${Number(receipt.amount || 0).toFixed(2)}
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center text-sm text-blue-600 mb-1">
                                <MethodIcon className="h-4 w-4 mr-1" />
                                Payment Method
                            </div>
                            <div className="text-xl font-semibold text-blue-900">
                                {getPaymentMethodLabel(receipt.payment_method)}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Receipt Date */}
                        <div className="flex items-start space-x-3">
                            <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-sm text-gray-500">Receipt Date</div>
                                <div className="text-sm font-medium text-gray-900">
                                    {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '-'}
                                </div>
                            </div>
                        </div>

                        {/* Type */}
                        <div className="flex items-start space-x-3">
                            <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-sm text-gray-500">Transaction Type</div>
                                <div className="text-sm font-medium text-gray-900 capitalize">
                                    {receipt.type || '-'}
                                </div>
                            </div>
                        </div>

                        {/* Created At */}
                        <div className="flex items-start space-x-3">
                            <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-sm text-gray-500">Created</div>
                                <div className="text-sm font-medium text-gray-900">
                                    {receipt.created_at ? new Date(receipt.created_at).toLocaleString() : '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Transaction Card */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center">
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Related Transaction
                    </h3>
                </div>
                <div className="px-6 py-4">
                    {receiptable ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Transaction ID */}
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">Transaction</div>
                                    <button
                                        onClick={() => {
                                            if (receipt.type === 'sale') {
                                                navigate(`/sales/${receipt.receiptable_id}`);
                                            } else if (receipt.type === 'purchase') {
                                                navigate(`/purchases/${receipt.receiptable_id}`);
                                            }
                                        }}
                                        className="text-sm font-medium text-blue-600 hover:underline"
                                    >
                                        {receipt.type === 'sale' ? 'Sale' : 'Purchase'} #{receipt.receiptable_id}
                                    </button>
                                </div>

                                {/* Customer/Supplier */}
                                {(receiptable.customer || receiptable.supplier) && (
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1 flex items-center">
                                            <User className="h-3 w-3 mr-1" />
                                            {receipt.type === 'sale' ? 'Customer' : 'Supplier'}
                                        </div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {receiptable.customer?.name || receiptable.supplier?.name || '-'}
                                        </div>
                                    </div>
                                )}

                                {/* Transaction Total */}
                                <div>
                                    <div className="text-sm text-gray-500 mb-1 flex items-center">
                                        <DollarSign className="h-3 w-3 mr-1" />
                                        Transaction Total
                                    </div>
                                    <div className="text-sm font-medium text-gray-900">
                                        ${Number(receiptable.total_amount || receiptable.net_amount || 0).toFixed(2)}
                                    </div>
                                </div>

                                {/* Transaction Status */}
                                <div>
                                    <div className="text-sm text-gray-500 mb-1 flex items-center">
                                        <Package className="h-3 w-3 mr-1" />
                                        Status
                                    </div>
                                    <div className="text-sm font-medium text-gray-900 capitalize">
                                        {receiptable.status || receiptable.payment_status || '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-6 text-gray-500">
                            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>Related transaction details not available.</p>
                            {receipt.receiptable_id && (
                                <p className="text-sm mt-1">
                                    {receipt.type === 'sale' ? 'Sale' : 'Purchase'} #{receipt.receiptable_id}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReceiptDetail;
